/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * reCAPTCHA configuration helpers — site key resolution and enable/disable checks.
 */

import { getFlag } from './feature-flag-registry';

export function resolveAntiBotProviderId(): string | undefined {
  const windowEnv = typeof window !== 'undefined' && (window as any).__ENV__;
  return (windowEnv && windowEnv['ANTI_BOT_PROVIDER_ID'])
    || import.meta.env.ANTI_BOT_PROVIDER_ID
    || undefined;
}

/**
 * Resolve the CHECKBOX site key used for the anti-bot challenge step-up
 * widget. Deliberately has no fallback to `resolveAntiBotProviderId()` (D11)
 * -- that SCORE-type key renders no visible checkbox (no bframe), which is
 * exactly the recovery dead end this flow exists to fix.
 */
export function resolveAntiBotChallengeKey(): string | undefined {
  const windowEnv = typeof window !== 'undefined' && (window as any).__ENV__;
  return (windowEnv && windowEnv['ANTI_BOT_CHALLENGE_KEY'])
    || import.meta.env.ANTI_BOT_CHALLENGE_KEY
    || undefined;
}

/**
 * Resolve the anti-bot bypass token from the runtime environment.
 *
 * Used for local development against a non-production backend that accepts a
 * shared bypass token in the X-Anti-Bot header instead of a real reCAPTCHA
 * challenge. Reads window.__ENV__ first, then import.meta.env.
 *
 * @returns {string | undefined} The bypass token, or undefined when unset.
 */
export function resolveAntiBotBypassToken(): string | undefined {
  const windowEnv = typeof window !== 'undefined' && (window as any).__ENV__;
  return (windowEnv && windowEnv['ANTI_BOT_BYPASS_TOKEN'])
    || import.meta.env.ANTI_BOT_BYPASS_TOKEN
    || undefined;
}

/**
 * Check if anti-bot protection is mocked (disabled).
 *
 * Delegates to the FF_MOCK_ANTIBOT feature flag from the registry.
 * When the flag is ON (true), anti-bot validation is bypassed.
 * Runtime overrides from the dev cockpit are respected.
 *
 * @returns {boolean} True if anti-bot should be bypassed
 */
export function isRecaptchaDisabled(): boolean {
  return getFlag('FF_MOCK_ANTIBOT');
}

/**
 * Check if reCAPTCHA is enabled and should be used.
 *
 * @returns {boolean} True if reCAPTCHA is enabled and configured
 */
export function isRecaptchaEnabled(): boolean {
  if (isRecaptchaDisabled()) {
    return false;
  }
  return isRecaptchaConfigured();
}

/**
 * Check if reCAPTCHA is configured.
 *
 * @returns {boolean} True if reCAPTCHA site key is configured
 */
export function isRecaptchaConfigured(): boolean {
  const siteKey = resolveAntiBotProviderId();
  return !!siteKey && siteKey !== '';
}

/**
 * Get reCAPTCHA site key from environment.
 *
 * @returns {string | undefined} Site key or undefined if not configured
 */
export function getRecaptchaSiteKey(): string | undefined {
  const siteKey = resolveAntiBotProviderId();
  return siteKey && siteKey !== '' ? siteKey : undefined;
}

/**
 * HTTP methods the backend treats as safe/idempotent and never validates the
 * X-Anti-Bot header for (see OIDCClientProtectedRoute.SAFE_METHODS on the
 * backend). Minting a real reCAPTCHA token for these methods is pure wasted
 * traffic — the backend ignores it.
 */
const SAFE_ANTIBOT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * @param method HTTP method of the outgoing request, e.g. 'GET' or 'POST'.
 * @returns Whether this method is exempt from anti-bot token minting because
 *          the backend never validates it for that method.
 */
export function isSafeAntiBotMethod(method?: string): boolean {
  return !!method && SAFE_ANTIBOT_METHODS.has(method.toUpperCase());
}

export interface GRecaptchaEnterprise {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

/**
 * Retrieve the reCAPTCHA Enterprise API object from the global window scope.
 *
 * @returns The enterprise API, or null when the script has not loaded yet
 */
export function getGrecaptchaEnterprise(): GRecaptchaEnterprise | null {
  const w = window as unknown as { grecaptcha?: { enterprise?: GRecaptchaEnterprise } };
  return w.grecaptcha?.enterprise ?? null;
}
